/* eslint-disable max-lines */
import { evidenceDisclaimerModalOpenAtom } from '@/atoms/evidenceProModalAtoms';
import { JurisdictionSelect } from '@/components/ui/jurisdiction-select/JurisdictionSelect';
import {
  DialogDrawer,
  DialogDrawerBody,
  DialogDrawerContent,
  DialogDrawerDescription,
  DialogDrawerFooter,
  DialogDrawerTitle,
} from '@/components/ui/mobile-responsive-dialog-drawer';
import { anyDialogDrawerOpenAtom } from '@/components/ui/mobile-responsive-dialog-drawer/atoms';
import ClinicianTypeSelect from '@/components/ui/select/clinician-type-select';
import { useMutateUpdateSettings } from '@/hooks/mutation/useMutateUpdateSettings';
import { useMutateUpdateUser } from '@/hooks/mutation/useMutateUpdateUser';
import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import {
  trackEvidenceOnboardingDismissed,
  trackEvidenceOnboardingSaved,
  trackEvidenceOnboardingSkipped,
  trackEvidenceOnboardingViewed,
} from '@/lib/analytics/scribe/evidence';
import { type ClinicianTypeInput } from '@/lib/types';
import { Button } from '@heidi/design-system/button';
import { TypographyP2, TypographyP2Bold } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { SignupSource } from '@heidi/schema/ml-scribe';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isJurisdictionValidForCountry } from './utils';

const hasDismissedOnboardingAtom = atom(false);

interface EvidenceSettingsExtended {
  hide_suggestions?: boolean;
  has_seen_evidence_onboarding?: boolean;
  country?: string | null;
  jurisdiction?: string | null;
}

interface EvidenceOnboardingModalProps {
  /** Whether we're on the full-screen evidence page (required condition) */
  isFullScreenEvidence: boolean;
}

export const EvidenceOnboardingModal = memo(
  ({ isFullScreenEvidence }: EvidenceOnboardingModalProps) => {
    const { t } = useTranslations();
    const anyDialogDrawerOpen = useAtomValue(anyDialogDrawerOpenAtom);
    // UK/EU disclaimer modal takes priority - wait for it to close before showing onboarding
    const isDisclaimerModalOpen = useAtomValue(evidenceDisclaimerModalOpenAtom);

    const { data: user } = useQueryUser((data) => {
      const evidenceSettings = data?.evidence_settings as
        | EvidenceSettingsExtended
        | undefined;
      return {
        hasSeenEvidenceOnboarding: evidenceSettings?.has_seen_evidence_onboarding,
        specialtyId: data?.specialty_id,
        otherSpecialty: data?.other_specialty,
        accountCountry: data?.country,
        evidenceCountry: evidenceSettings?.country,
        evidenceJurisdiction: evidenceSettings?.jurisdiction,
        // Include organisation_info to check if initial onboarding is complete
        hasCompletedOnboarding: !!data?.organisation_info?.name,
        signupSource: data?.signup_source ?? SignupSource.scribe,
      };
    });
    const { mutate: updateSettings } = useMutateUpdateSettings({ showToast: false });
    const { mutate: updateUser } = useMutateUpdateUser();
    const { data: specialties } = useQuerySpecialties((data) => data.specialties);

    // Local state for form
    const [selectedSpecialtyId, setSelectedSpecialtyId] = useState<string | null>(null);
    const [selectedOtherSpecialty, setSelectedOtherSpecialty] = useState<
      string | undefined
    >(undefined);
    const [selectedJurisdiction, setSelectedJurisdiction] = useState<string | null>(null);
    const hasInitializedFormRef = useRef(false);

    const jurisdictionCountryName = user?.evidenceCountry ?? user?.accountCountry;

    // Initialize form state from user data
    useEffect(() => {
      if (!user || hasInitializedFormRef.current) {
        return;
      }

      if (user.specialtyId) {
        setSelectedSpecialtyId(user.specialtyId);
      }
      if (user.otherSpecialty) {
        setSelectedOtherSpecialty(user.otherSpecialty);
      }
      if (user.evidenceJurisdiction) {
        setSelectedJurisdiction(user.evidenceJurisdiction);
      }
      hasInitializedFormRef.current = true;
    }, [user]);

    const shouldShowModal = useMemo(() => {
      const result =
        isFullScreenEvidence &&
        !anyDialogDrawerOpen &&
        !isDisclaimerModalOpen &&
        user !== undefined &&
        user?.hasCompletedOnboarding &&
        user?.hasSeenEvidenceOnboarding !== true &&
        user?.signupSource !== SignupSource.evidence;
      return result;
    }, [isFullScreenEvidence, anyDialogDrawerOpen, isDisclaimerModalOpen, user]);

    const [open, setOpen] = useState(false);
    const hasDismissed = useAtomValue(hasDismissedOnboardingAtom);
    const setHasDismissed = useSetAtom(hasDismissedOnboardingAtom);

    // Update open state when conditions change
    useEffect(() => {
      if (shouldShowModal && !open && !hasDismissed) {
        setOpen(true);
        // Track modal viewed
        trackEvidenceOnboardingViewed({
          has_existing_specialty: !!user?.specialtyId,
          has_existing_jurisdiction: !!user?.evidenceJurisdiction,
        });
      }
    }, [
      shouldShowModal,
      open,
      hasDismissed,
      user?.specialtyId,
      user?.evidenceJurisdiction,
    ]);

    // Close onboarding if disclaimer opens (disclaimer takes priority).
    // Don't mark as seen — the onboarding will re-open after the disclaimer is closed.
    useEffect(() => {
      if (isDisclaimerModalOpen && open) {
        setOpen(false);
      }
    }, [isDisclaimerModalOpen, open]);

    // Build the selectedSpecialty object for ClinicianTypeSelect
    const selectedSpecialtyObj = useMemo(() => {
      if (!selectedSpecialtyId || !specialties) return undefined;
      // Handle "Other" specialty first, before looking up in specialties
      if (selectedSpecialtyId === 'other' || selectedOtherSpecialty) {
        return { id: 'other', name: 'Other', group: { id: 'other', name: 'Other' } };
      }
      const specialty = specialties.find((s) => s.id === selectedSpecialtyId);
      if (!specialty) return undefined;
      return {
        id: specialty.id,
        name: specialty.localizedName,
        group: { id: specialty.group?.id || '', name: specialty.group?.name || '' },
      };
    }, [selectedSpecialtyId, specialties, selectedOtherSpecialty]);

    const handleSpecialtyChange = useCallback((specialty: ClinicianTypeInput) => {
      setSelectedSpecialtyId(specialty.id);
      if (specialty.id !== 'other') {
        setSelectedOtherSpecialty(undefined);
      }
    }, []);

    const handleOtherSpecialtyChange = useCallback((value?: string) => {
      setSelectedOtherSpecialty(value);
    }, []);

    const handleJurisdictionChange = useCallback((jurisdictionCode: string) => {
      setSelectedJurisdiction(jurisdictionCode);
    }, []);

    const handleSave = useCallback(async () => {
      const specialtyChanged =
        !!selectedSpecialtyId && selectedSpecialtyId !== user?.specialtyId;
      const isValid =
        !!selectedJurisdiction &&
        (await isJurisdictionValidForCountry({
          jurisdictionCode: selectedJurisdiction,
          countryName: jurisdictionCountryName,
        }));
      const validatedJurisdiction = isValid ? selectedJurisdiction : null;
      const jurisdictionChanged =
        !!validatedJurisdiction && validatedJurisdiction !== user?.evidenceJurisdiction;
      const shouldPersistEvidenceCountry =
        !!jurisdictionCountryName && !user?.evidenceCountry;

      // Update specialty if changed
      if (specialtyChanged) {
        updateSettings({
          specialty_id: selectedSpecialtyId,
          other_specialty: selectedOtherSpecialty ?? null,
        });
      }

      // Update Evidence location — persist country when seeding from account
      // country for jurisdiction validation (not a read-path fallback).
      if (jurisdictionChanged) {
        updateUser({
          evidence_settings: {
            jurisdiction: validatedJurisdiction,
            ...(shouldPersistEvidenceCountry && {
              country: jurisdictionCountryName,
            }),
          },
        });
      }

      // Mark as seen via updateUser (optimistic cache update + retry:3)
      updateUser({
        evidence_settings: {
          has_seen_evidence_onboarding: true,
        },
      });

      // Track save action
      trackEvidenceOnboardingSaved({
        specialty_id: selectedSpecialtyId ?? undefined,
        jurisdiction: validatedJurisdiction ?? undefined,
        specialty_changed: specialtyChanged,
        jurisdiction_changed: jurisdictionChanged,
      });

      setHasDismissed(true);
      setOpen(false);
    }, [
      selectedSpecialtyId,
      selectedOtherSpecialty,
      selectedJurisdiction,
      user?.specialtyId,
      user?.evidenceJurisdiction,
      user?.evidenceCountry,
      jurisdictionCountryName,
      updateSettings,
      updateUser,
      setHasDismissed,
    ]);

    const markAsSeen = useCallback(() => {
      updateUser({
        evidence_settings: {
          has_seen_evidence_onboarding: true,
        },
      });
    }, [updateUser]);

    const handleSkip = useCallback(() => {
      markAsSeen();
      trackEvidenceOnboardingSkipped();
      setHasDismissed(true);
      setOpen(false);
    }, [markAsSeen, setHasDismissed]);

    const handleOpenChange = useCallback(
      (newOpen: boolean) => {
        if (!newOpen) {
          // If the disclaimer forced us closed, don't mark as seen so the
          // onboarding can re-appear once the disclaimer is dismissed.
          if (!isDisclaimerModalOpen) {
            markAsSeen();
            trackEvidenceOnboardingDismissed();
            setHasDismissed(true);
          }
        }
        setOpen(newOpen);
      },
      [markAsSeen, setHasDismissed, isDisclaimerModalOpen]
    );

    return (
      <DialogDrawer open={open} onOpenChange={handleOpenChange}>
        <DialogDrawerContent
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="flex w-full max-w-lg flex-col gap-6 p-6"
          dismissible={true}
        >
          <DialogDrawerBody className="flex flex-col gap-4 p-0">
            <div className="flex flex-col gap-2">
              <DialogDrawerTitle>{t('chat.onboarding.title')}</DialogDrawerTitle>
              <DialogDrawerDescription>
                {t('chat.onboarding.description')}
              </DialogDrawerDescription>
            </div>

            {/* Specialty Select */}
            <div className="flex flex-col gap-2">
              <TypographyP2Bold>{t('chat.header.specialty')}</TypographyP2Bold>
              <ClinicianTypeSelect
                selectedSpecialty={selectedSpecialtyObj}
                setSelectedSpecialty={handleSpecialtyChange}
                otherSpecialty={selectedOtherSpecialty}
                setOtherSpecialty={handleOtherSpecialtyChange}
                settingsLayout={true}
              />
            </div>

            {/* Jurisdiction Select */}
            <div className="flex flex-col gap-2">
              <TypographyP2Bold>{t('chat.onboarding.stateRegion')}</TypographyP2Bold>
              <JurisdictionSelect
                countryName={jurisdictionCountryName}
                value={selectedJurisdiction}
                onChange={handleJurisdictionChange}
                className="w-full"
              />
              {!jurisdictionCountryName && (
                <TypographyP2 className="text-text-secondary">
                  {t('chat.onboarding.countryNotSet')}
                </TypographyP2>
              )}
            </div>
          </DialogDrawerBody>

          <DialogDrawerFooter className="flex flex-col-reverse gap-2 p-0 sm:flex-row sm:justify-end">
            <Button size="lg" variant="ghost" onClick={handleSkip}>
              {t('chat.onboarding.skipForNow')}
            </Button>
            <Button
              size="lg"
              onClick={() => {
                void handleSave();
              }}
            >
              {t('chat.onboarding.savePreferences')}
            </Button>
          </DialogDrawerFooter>
        </DialogDrawerContent>
      </DialogDrawer>
    );
  }
);

EvidenceOnboardingModal.displayName = 'EvidenceOnboardingModal';
